import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import useIsMobile from "@/hooks/useIsMobile";
import { useQueryParams } from "@/utils/useQueryParams";

const GlobalPagination = ({ paginationData }) => {
  const isMobile = useIsMobile(400);
  const { currentPage, totalPages } = paginationData;
  const { limit, setQueryParams } = useQueryParams();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setQueryParams({ page: newPage, limit });
      scrollToTop();
    }
  };

  const shouldShowFirst = isMobile
    ? currentPage > 3
    : currentPage >= 4 && totalPages > 5;

  const shouldShowLast = isMobile
    ? currentPage < totalPages - 1
    : currentPage < totalPages - 3 && totalPages > 5;

  return (
    <div className="py-[27px] w-full flex justify-center">
      <Pagination className="flex justify-center">
        <PaginationContent>
          {/* Previous Button */}
          <PaginationItem>
            <PaginationPrevious
              onClick={() => handlePageChange(currentPage - 1)}
              className={currentPage === 1 ? "disabled" : ""}
              style={{
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                backgroundColor: currentPage === 1 ? "#ccc" : "",
              }}
              disabled={currentPage === 1}
            />
          </PaginationItem>

          {/* First Page and Ellipsis */}
          {shouldShowFirst && (
            <>
              <PaginationItem>
                <PaginationLink
                  isActive={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  style={{ cursor: "pointer" }}
                >
                  1
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            </>
          )}

          {/* Dynamic Page Numbers */}
          {(() => {
            const maxVisible = isMobile ? 3 : 5;
            let startPage = Math.max(currentPage - 1, 1);
            let endPage = Math.min(currentPage + 1, totalPages);

            // Adjust start/end if near the beginning or end
            if (currentPage <= 2) {
              startPage = 1;
              endPage = isMobile ? Math.min(3, totalPages) : Math.min(5, totalPages);
            }

            if (currentPage >= totalPages - 1) {
              endPage = totalPages;
              startPage = isMobile
                ? Math.max(totalPages - 2, 1)
                : Math.max(totalPages - 4, 1);
            }

            return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
              const page = startPage + i;
              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={currentPage === page}
                    onClick={() => handlePageChange(page)}
                    style={{ cursor: "pointer" }}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              );
            });
          })()}

          {/* Ellipsis and Last Page */}
          {shouldShowLast && (
            <>
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  isActive={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                  style={{ cursor: "pointer" }}
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          {/* Next Button */}
          <PaginationItem>
            <PaginationNext
              onClick={() => handlePageChange(currentPage + 1)}
              className={currentPage === totalPages ? "disabled" : ""}
              style={{
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                backgroundColor: currentPage === totalPages ? "#ccc" : "",
              }}
              disabled={currentPage === totalPages}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default GlobalPagination;
